// ============================================================================
//  CrmCommissionsPage.tsx — Page complète des commissions
//  Workflow : PENDING → MANAGER_APPROVED → HR_APPROVED → FINANCE_APPROVED
//             → INTEGRATE → PAID
//  + Simulation avant deal, Contestation, Réouverture
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DollarSign, Check, X, AlertCircle, RefreshCw, ChevronDown, ChevronUp,
  Calculator, Package, CreditCard, AlertTriangle, FileText, Plus,
} from 'lucide-react'
import { crmApi, type Commission } from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore }          from '@/stores/auth.store'
import { EmployeeCombobox } from '@/modules/crm/components/CrmComboboxes'

const Spinner = () => <div className="w-4 h-4 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
const inputCls = 'w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none focus:ring-1 focus:ring-[var(--brand-orange)]'

const fmtMoney = (n?: any, currency = 'XOF') => {
  const v = typeof n === 'number' ? n : parseFloat(n ?? '0') || 0
  return new Intl.NumberFormat('fr-SN', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v)
}
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('fr-SN') : '—'

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  PENDING:          { label: 'En attente',       color: 'var(--tx2)', bg: '#D6D2EC20' },
  MANAGER_APPROVED: { label: 'Approuvé manager', color: 'var(--blue-d)', bg: '#BAE6FD40' },
  HR_APPROVED:      { label: 'Approuvé RH',      color: '#4F46E5', bg: '#C7D2FE40' },
  FINANCE_APPROVED: { label: 'Approuvé finance', color: '#7C3AED', bg: '#DDD6FE40' },
  INTEGRATED:       { label: 'Intégré paie',     color: '#059669', bg: '#D1FAE540' },
  PAID:             { label: 'Payé',             color: '#16A34A', bg: '#BBF7D040' },
  CONTESTED:        { label: 'Contesté',         color: '#D97706', bg: '#FDE68A40' },
  REJECTED:         { label: 'Rejeté',           color: '#DC2626', bg: '#FEE2E240' },
}

// Workflow steps visuels
const WORKFLOW_STEPS = ['PENDING', 'MANAGER_APPROVED', 'HR_APPROVED', 'FINANCE_APPROVED', 'INTEGRATED', 'PAID']

function WorkflowBar({ status }: { status: string }) {
  const idx = WORKFLOW_STEPS.indexOf(status)
  if (idx === -1) return null
  return (
    <div className="flex items-center gap-1 mt-2">
      {WORKFLOW_STEPS.map((s, i) => {
        const done    = i < idx
        const current = i === idx
        const cfg     = STATUS_CONFIG[s]
        return (
          <div key={s} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${done ? 'bg-[#10B981]' : current ? 'bg-[var(--blue)]' : 'bg-[var(--bd)]'}`} />
            {i < WORKFLOW_STEPS.length - 1 && <div className={`w-4 h-px ${done ? 'bg-[#10B981]' : 'bg-[var(--bd)]'}`} />}
          </div>
        )
      })}
    </div>
  )
}

export function CommissionsPage() {
  const [statusFilter, setStatusFilter] = useState('')
  const [contestModal, setContestModal] = useState<string | null>(null)
  const [integrateModal, setIntegrateModal] = useState<Commission | null>(null)
  const [simModal, setSimModal]          = useState(false)
  const [expandedId, setExpandedId]      = useState<string | null>(null)
  const queryClient = useQueryClient()
  const addNotif    = useNotificationStore(s => s.addNotification)
  const { hasPermission } = useAuthStore()
  const canConfigure = hasPermission('crm:configure')

  const { data, isLoading } = useQuery({
    queryKey: ['crm-commissions', statusFilter],
    queryFn:  () => crmApi.listCommissions({ status: statusFilter || undefined, limit: 50 }),
  })
  const commissions: Commission[] = data?.items ?? []

  const notify = (title: string, type: 'success' | 'error' = 'success') =>
    addNotif({ id: Date.now().toString(), type, title })

  const mutate = (fn: () => Promise<any>, successMsg: string) => ({
    mutationFn: fn,
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); notify(successMsg) },
    onError:    (err: any) => notify(err?.response?.data?.message ?? 'Erreur', 'error'),
  })

  const validateMutation = useMutation(mutate(
    () => Promise.resolve(), // placeholder, called inline
    'Commission mise à jour',
  ))

  const reopenMutation = useMutation({
    mutationFn: (id: string) => crmApi.reopenCommission(id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); notify('Commission remise en circuit') },
    onError:    (err: any) => notify(err?.response?.data?.message ?? 'Erreur', 'error'),
  })

  const paidMutation = useMutation({
    mutationFn: (id: string) => crmApi.markCommissionPaid(id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); notify('Commission marquée comme payée') },
    onError:    (err: any) => notify(err?.response?.data?.message ?? 'Erreur', 'error'),
  })

  const doValidate = (id: string, action: string) =>
    crmApi.validateCommission(id, { action })
      .then(() => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); notify('Commission mise à jour') })
      .catch((err: any) => notify(err?.response?.data?.message ?? 'Erreur', 'error'))

  const doContest = (id: string, reason: string) =>
    crmApi.contestCommission(id, reason)
      .then(() => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); setContestModal(null); notify('Commission contestée') })
      .catch((err: any) => notify(err?.response?.data?.message ?? 'Erreur', 'error'))

  // KPIs rapides
  const kpis = {
    pending:  commissions.filter(c => c.status === 'PENDING').length,
    total:    commissions.reduce((s, c) => s + Number(c.amount), 0),
    paid:     commissions.filter(c => c.status === 'PAID').reduce((s, c) => s + Number(c.amount), 0),
    contested: commissions.filter(c => c.status === 'CONTESTED').length,
  }

  return (
    <div className="space-y-5 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>Commissions</h1>
          <p style={{ fontSize: 13, color: 'var(--tx3)', marginTop: 2 }}>Suivi et validation du workflow commission</p>
        </div>
        <button onClick={() => setSimModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-[var(--bd)] text-[var(--brand-amber)] hover:bg-[#F4F2FC]">
          <Calculator className="w-4 h-4" /> Simuler
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'En attente',  value: kpis.pending,             icon: RefreshCw, color: 'var(--blue-d)', bg: '#EFF6FF' },
          { label: 'Total volume', value: fmtMoney(kpis.total),    icon: DollarSign, color: '#7C3AED', bg: '#F5F3FF', isText: true },
          { label: 'Payées',      value: fmtMoney(kpis.paid),      icon: Check,      color: '#059669', bg: '#ECFDF5', isText: true },
          { label: 'Contestées',  value: kpis.contested,           icon: AlertTriangle, color: '#D97706', bg: '#FFFBEB' },
        ].map(k => (
          <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg shrink-0" style={{ background: k.bg }}>
              <k.icon className="w-4 h-4" style={{ color: k.color }} />
            </div>
            <div>
              <p className={`font-bold text-[var(--brand-dark)] ${k.isText ? 'text-sm' : 'text-xl'}`}>{k.value}</p>
              <p className="text-xs text-[var(--tx3)]">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtre */}
      <div className="flex gap-2 flex-wrap">
        {[{ v: '', l: 'Toutes' }, ...Object.entries(STATUS_CONFIG).map(([v, c]) => ({ v, l: c.label }))].map(opt => (
          <button key={opt.v} onClick={() => setStatusFilter(opt.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === opt.v ? 'bg-[var(--sb-bg)] text-[var(--blue)]' : 'bg-[#F4F2FC] text-[var(--brand-amber)]'}`}>
            {opt.l}
          </button>
        ))}
      </div>

      {/* Liste */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : commissions.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-3">
          <DollarSign className="w-12 h-12 text-[var(--tx3)]" />
          <p className="text-[var(--tx3)] text-sm">Aucune commission{statusFilter ? ' avec ce statut' : ''}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {commissions.map(c => {
            const cfg     = STATUS_CONFIG[c.status] ?? STATUS_CONFIG.PENDING
            const isExpanded = expandedId === c.id
            const detail    = (c.calculationDetail ?? {}) as any

            return (
              <div key={c.id} className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden hover:border-[var(--blue)]/30 transition-all">
                {/* Ligne principale */}
                <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : c.id)}>
                  {/* Statut */}
                  <div className="shrink-0">
                    <span className="text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap"
                      style={{ background: cfg.bg, color: cfg.color }}>
                      {cfg.label}
                    </span>
                  </div>

                  {/* Info commerciale */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--brand-dark)] truncate">
                      {c.employee?.user?.firstName} {c.employee?.user?.lastName}
                    </p>
                    <p className="text-xs text-[var(--tx3)] truncate">{c.opportunity?.title ?? '—'}</p>
                  </div>

                  {/* Montant deal */}
                  <div className="text-right shrink-0 hidden md:block">
                    <p className="text-xs text-[var(--tx3)]">Deal</p>
                    <p className="text-sm text-[var(--tx2)]">{fmtMoney(c.opportunity?.amount)}</p>
                  </div>

                  {/* Commission */}
                  <div className="text-right shrink-0">
                    <p className="text-xs text-[var(--tx3)]">Commission</p>
                    <p className="text-sm font-bold text-[#059669]">{fmtMoney(c.amount, c.currency)}</p>
                  </div>

                  <div className="text-[var(--tx3)]">
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </div>

                {/* Détail déplié */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0" style={{ borderTop: '1px solid var(--bd)' }}>
                    <div className="pt-3 space-y-4">
                      {/* Workflow */}
                      <div>
                        <p className="text-xs text-[var(--tx3)] mb-1">Progression du workflow</p>
                        <WorkflowBar status={c.status} />
                      </div>

                      {/* Détails calcul */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {detail.planName && <div><span className="text-[var(--tx3)]">Plan : </span><span className="text-[var(--brand-dark)] font-medium">{detail.planName}</span></div>}
                        {detail.planType && <div><span className="text-[var(--tx3)]">Type : </span><span className="text-[var(--brand-dark)]">{detail.planType}</span></div>}
                        {detail.tier?.rate && <div><span className="text-[var(--tx3)]">Taux : </span><span className="text-[var(--brand-dark)]">{detail.tier.rate}%</span></div>}
                        {c.managerApprovedAt && <div><span className="text-[var(--tx3)]">Approuvé manager : </span><span className="text-[var(--brand-dark)]">{fmtDate(c.managerApprovedAt)}</span></div>}
                        {c.hrApprovedAt && <div><span className="text-[var(--tx3)]">Approuvé RH : </span><span className="text-[var(--brand-dark)]">{fmtDate(c.hrApprovedAt)}</span></div>}
                        {c.financeApprovedAt && <div><span className="text-[var(--tx3)]">Approuvé finance : </span><span className="text-[var(--brand-dark)]">{fmtDate(c.financeApprovedAt)}</span></div>}
                        {c.paidAt && <div><span className="text-[var(--tx3)]">Payé le : </span><span className="text-[#059669] font-medium">{fmtDate(c.paidAt)}</span></div>}
                        {c.payslipId && <div className="col-span-2"><span className="text-[var(--tx3)]">Bulletin paie : </span><span className="text-[var(--brand-amber)] font-mono text-[10px]">{c.payslipId}</span></div>}
                      </div>

                      {/* Raison contestation */}
                      {c.contestReason && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs font-semibold text-amber-700 mb-1">⚠ Motif de contestation</p>
                          <p className="text-xs text-amber-700">{c.contestReason}</p>
                        </div>
                      )}

                      {/* Actions */}
                      {canConfigure && (
                        <div className="flex flex-wrap gap-2">
                          {c.status === 'PENDING' && (
                            <>
                              <ActionBtn onClick={() => doValidate(c.id, 'MANAGER_APPROVED')} color="emerald" icon={Check}>Approuver (Manager)</ActionBtn>
                              <ActionBtn onClick={() => doValidate(c.id, 'REJECTED')} color="red" icon={X}>Rejeter</ActionBtn>
                            </>
                          )}
                          {c.status === 'MANAGER_APPROVED' && (
                            <ActionBtn onClick={() => doValidate(c.id, 'HR_APPROVED')} color="indigo" icon={Check}>Valider RH</ActionBtn>
                          )}
                          {c.status === 'HR_APPROVED' && (
                            <ActionBtn onClick={() => doValidate(c.id, 'FINANCE_APPROVED')} color="purple" icon={Check}>Valider Finance</ActionBtn>
                          )}
                          {c.status === 'FINANCE_APPROVED' && (
                            <ActionBtn onClick={() => setIntegrateModal(c)} color="teal" icon={Package}>Intégrer à la paie</ActionBtn>
                          )}
                          {c.status === 'INTEGRATED' && (
                            <ActionBtn onClick={() => window.confirm('Marquer comme payée ?') && paidMutation.mutate(c.id)} color="green" icon={CreditCard}>Marquer payée</ActionBtn>
                          )}
                          {c.status === 'CONTESTED' && (
                            <ActionBtn onClick={() => window.confirm('Remettre en PENDING ?') && reopenMutation.mutate(c.id)} color="orange" icon={RefreshCw}>Remettre en circuit</ActionBtn>
                          )}
                        </div>
                      )}

                      {/* Contester — visible pour tout le monde en cours de validation */}
                      {['PENDING', 'MANAGER_APPROVED', 'HR_APPROVED'].includes(c.status) && (
                        <button onClick={() => setContestModal(c.id)}
                          className="text-xs text-amber-600 hover:text-amber-700 underline">
                          Contester cette commission
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Modal contestation */}
      {contestModal && (
        <ContestModal onClose={() => setContestModal(null)}
          onSubmit={reason => doContest(contestModal, reason)} />
      )}

      {/* Modal intégration paie */}
      {integrateModal && (
        <IntegrateModal commission={integrateModal}
          onClose={() => setIntegrateModal(null)}
          onDone={() => { queryClient.invalidateQueries({ queryKey: ['crm-commissions'] }); setIntegrateModal(null); notify('Commission intégrée au bulletin') }} />
      )}

      {/* Modal simulation */}
      {simModal && <SimulationModal onClose={() => setSimModal(false)} />}
    </div>
  )
}

// ── Bouton action coloré ──────────────────────────────────────────────────────

function ActionBtn({ onClick, color, icon: Icon, children }: { onClick: () => void; color: string; icon: any; children: React.ReactNode }) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    red:     'bg-red-50 text-red-700 hover:bg-red-100',
    indigo:  'bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    purple:  'bg-purple-50 text-purple-700 hover:bg-purple-100',
    teal:    'bg-teal-50 text-teal-700 hover:bg-teal-100',
    green:   'bg-green-50 text-green-700 hover:bg-green-100',
    orange:  'bg-amber-50 text-amber-700 hover:bg-amber-100',
  }
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[color]}`}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  )
}

// ── Modal contestation ────────────────────────────────────────────────────────

function ContestModal({ onClose, onSubmit }: { onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('')
  return (
    <ModalShell title="Contester la commission" onClose={onClose}>
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-700">Expliquez pourquoi vous contestez cette commission (minimum 10 caractères). Le manager sera notifié.</p>
      </div>
      <div>
        <label className="block text-xs text-[var(--tx3)] mb-1">Motif *</label>
        <textarea className={inputCls + ' resize-none'} rows={4} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Ex: Le montant ne correspond pas au deal signé le 15/03. La valeur contractuelle était de 2 500 000 XOF…" />
        <p className="text-[10px] text-[var(--tx3)] mt-1">{reason.length}/10 minimum</p>
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => onSubmit(reason)} disabled={reason.trim().length < 10}
          className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
          <AlertCircle className="w-4 h-4" /> Contester
        </button>
      </div>
    </ModalShell>
  )
}

// ── Modal intégration paie ────────────────────────────────────────────────────

function IntegrateModal({ commission, onClose, onDone }: { commission: Commission; onClose: () => void; onDone: () => void }) {
  const [payslipId, setPayslipId] = useState('')
  const addNotif = useNotificationStore(s => s.addNotification)

  // Charger les bulletins disponibles pour cet employé
  const { data: payslips = [], isLoading } = useQuery({
    queryKey: ['payslips-for-commission', commission.employeeId],
    queryFn:  () => crmApi.getPayslipsForEmployee(commission.employeeId),
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => crmApi.integrateCommission(commission.id, payslipId),
    onSuccess:  onDone,
    onError:    (err: any) => addNotif({ id: Date.now().toString(), type: 'error', title: err?.response?.data?.message ?? 'Erreur' }),
  })

  return (
    <ModalShell title="Intégrer à la paie" onClose={onClose}>
      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
        <p className="text-xs text-teal-700">
          La commission de <strong>{fmtMoney(commission.amount, commission.currency)}</strong> sera ajoutée comme ligne "COMMISSION" dans le bulletin sélectionné.
        </p>
      </div>

      <div>
        <label className="block text-xs text-[var(--tx3)] mb-1">Bulletin de salaire cible *</label>
        {isLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-[var(--tx3)]"><Spinner /> Chargement…</div>
        ) : (payslips as any[]).length === 0 ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700">
              ⚠ Aucun bulletin en cours (DRAFT ou CALCULATED) pour cet employé. Initialisez d'abord la période de paie dans le module RH.
            </p>
          </div>
        ) : (
          <select className={inputCls} value={payslipId} onChange={e => setPayslipId(e.target.value)}>
            <option value="">Sélectionner un bulletin…</option>
            {(payslips as any[]).map((p: any) => (
              <option key={p.id} value={p.id}>
                {`${String(p.period?.month ?? '?').padStart(2, '0')}/${p.period?.year ?? '?'}`} — {p.status}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)]">Annuler</button>
        <button onClick={() => mutate()} disabled={isPending || !payslipId}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
          style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
          {isPending ? <Spinner /> : <Package className="w-4 h-4" />} Intégrer
        </button>
      </div>
    </ModalShell>
  )
}

// ── Modal simulation ──────────────────────────────────────────────────────────

function SimulationModal({ onClose }: { onClose: () => void }) {
  const [employeeId, setEmployeeId] = useState('')
  const [amount, setAmount]         = useState('')
  const [result, setResult]         = useState<any>(null)
  const [loading, setLoading]       = useState(false)

  const simulate = async () => {
    if (!employeeId || !amount) return
    setLoading(true)
    try {
      const res = await crmApi.simulateCommission(employeeId, Number(amount))
      setResult(res)
    } catch { /* ignore */ } finally { setLoading(false) }
  }

  return (
    <ModalShell title="Simuler une commission" onClose={onClose}>
      <p className="text-xs text-[var(--tx3)]">Calculez la commission estimée pour un employé et un montant de deal donné, selon son plan actif.</p>

      <div>
        <label className="block text-xs text-[var(--tx3)] mb-1">Commercial *</label>
        <EmployeeCombobox ariaLabel="Commercial" required value={employeeId || null} onChange={id => { setEmployeeId(id ?? ''); setResult(null) }} />
      </div>

      <div>
        <label className="block text-xs text-[var(--tx3)] mb-1">Montant du deal (XOF) *</label>
        <input className={inputCls} type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)}
          placeholder="Ex: 5000000" onKeyDown={e => e.key === 'Enter' && simulate()} />
      </div>

      <button onClick={simulate} disabled={loading || !employeeId || !amount}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ background: 'var(--brand-amber)', color: '#fff' }}>
        {loading ? <Spinner /> : <Calculator className="w-4 h-4" />} Calculer
      </button>

      {result && (
        <div className="p-4 bg-[#F4F2FC] border border-[var(--brand-amber)]/20 rounded-xl space-y-2">
          <p className="text-xs font-semibold text-[var(--brand-amber)] uppercase tracking-wider">Résultat</p>
          {result.plan ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--tx2)]">Plan : {result.plan}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[var(--tx2)]">Taux effectif</span>
                <span className="text-sm font-semibold text-[var(--brand-amber)]">{result.rate?.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between items-center border-t border-[var(--brand-amber)]/20 pt-2">
                <span className="text-base font-bold text-[var(--brand-dark)]">Commission estimée</span>
                <span className="text-xl font-bold text-[#059669]">{fmtMoney(result.amount)}</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--tx3)]">Aucun plan de commission actif pour cet employé.</p>
          )}
        </div>
      )}
    </ModalShell>
  )
}

// ── Shell modal ───────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--brand-dark)]">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-[var(--tx3)]" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

export default CommissionsPage
